"use client";

import { useMemo, useState, type ReactNode } from "react";
import { useSession } from "next-auth/react";
import { useTranslations } from "next-intl";
import { SafeImage } from "./SafeImage";
import { fallbackImage } from "@/data/properties";

export type PartnerTab = {
  id: string;
  name: string;
  logo: string | null;
  hidden?: boolean; // org is hidden from the public (owner/admin view only)
  panel: ReactNode; // server-rendered stays grid for this org
};

// Tabbed partner directory. Each tab is an organization (logo + name) and shows
// its own stays. When the viewer is an org admin, their organization is moved to
// the front and selected first.
export function PartnerTabs({ items }: { items: PartnerTab[] }) {
  const t = useTranslations("Partners");
  const { data: session } = useSession();
  const myOrgId = session?.user?.organizationId ?? null;

  // Admin's own org first; the rest keep their order.
  const ordered = useMemo(() => {
    if (!myOrgId) return items;
    const mine = items.filter((i) => i.id === myOrgId);
    if (mine.length === 0) return items;
    return [...mine, ...items.filter((i) => i.id !== myOrgId)];
  }, [items, myOrgId]);

  // Track the active tab by id so it follows re-ordering until the user clicks.
  const [activeId, setActiveId] = useState<string | null>(null);
  const active = ordered.find((i) => i.id === activeId) ?? ordered[0];

  if (ordered.length === 0) return null;

  return (
    <div>
      {/* Sticky org bar: docks under the sticky site header (~64px). Uses the
          page background token so it blends seamlessly in both themes and
          simply masks content scrolling beneath. On mobile the row scrolls
          horizontally (scrollbar hidden) instead of wrapping, keeping the
          stuck bar one line tall. */}
      <div className="sticky top-16 z-40 mb-8 overflow-x-auto border-b border-sand bg-[var(--color-background)] [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="flex w-max gap-2 sm:w-auto sm:flex-wrap">
        {ordered.map((tab) => {
          const isActive = tab.id === active.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveId(tab.id)}
              className={`-mb-px flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "border-forest-500 text-forest-700"
                  : "border-transparent text-stone hover:text-forest-600"
              }`}
            >
              {tab.logo ? (
                <SafeImage
                  src={tab.logo}
                  fallback={fallbackImage(tab.id)}
                  alt=""
                  width={24}
                  height={24}
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <span className="grid h-6 w-6 place-items-center rounded-full bg-forest-100 text-[11px] font-bold text-forest-700">
                  {tab.name.slice(0, 1).toUpperCase()}
                </span>
              )}
              {tab.name}
              {tab.hidden && (
                <span className="rounded-full bg-clay/15 px-2 py-0.5 text-[11px] font-medium text-clay">
                  {t("hiddenBadge")}
                </span>
              )}
            </button>
          );
        })}
        </div>
      </div>

      {/* Only the active org's panel is mounted. */}
      <div key={active.id}>{active.panel}</div>
    </div>
  );
}
